import { KeyValueCustomMultipleSelect, KeyValueCustomSelect, Pill } from "@smartsuite/react-ui/lib";
import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import * as yup from "yup";
import { getAssignedToMembers, getInternalAssignedToMembers } from "../../../../api";
import { FormFieldControl } from "../../../../components/FormFieldControl/FormFieldControl";
import { useFormContext } from "../../../../hooks/useFormContext";
import { useFormField } from "../../../../hooks/useFormField";
import { useIsPreview } from "../../../../hooks/useIsPreview";
import { usePrefillContext } from "../../../../hooks/usePrefillContext";
import { Fields } from "../../../../types/fields";
import type { Member } from "../../../../types/form";
import { $t } from "../../../../utils/intl";
import type { FormFieldProps } from "../../../types";
import { validateWithYup } from "../../../validator";
import { validatorText } from "../../../validator.text";
import { fieldsStateText, fieldsText } from "../../fields.text";
import type { AssignToFormValue } from "../assign-to.config";
import { prefillAssignTo } from "../assign-to.prefill";

import "./AssignedToFieldControl.sass";

export type AssignedToFieldControlProps = FormFieldProps;

export const AssignedToFieldControl: React.FunctionComponent<AssignedToFieldControlProps> = ({
  caption,
  field,
  formItem,
  label,
  name,
  required,
  isItemConditionSatisfied,
  readOnly,
}) => {
  const { isPreview } = useIsPreview();

  const requiredText = $t(validatorText.validationRequired);
  const formField = useFormField<Member[]>({
    name,
    validate: validateWithYup(
      yup
        .array()
        .of(yup.object())
        .when({
          is: () => formItem.required && !formItem.params.read_only && isItemConditionSatisfied,
          then: (at) => at.required(requiredText).min(1, requiredText),
        }),
    ),
  });

  const { accountId, config, passcode, isInternal, reportId } = useFormContext();

  const [options, setOptions] = useState<AssignToFormValue[]>([]);
  const { data, isLoading } = useQuery(
    `${Fields.userfield}_${field.slug}`,
    isInternal && reportId
      ? () => getInternalAssignedToMembers(accountId, reportId, field.slug)
      : async () => {
          if (accountId && config.sharing_hash && field.slug) {
            return await getAssignedToMembers(
              accountId,
              config.sharing_hash,
              field.slug,
              isPreview,
              passcode,
            );
          }
        },
    {
      staleTime: Infinity,
    },
  );

  const { prefillValues } = usePrefillContext();

  const prefillValue = prefillValues.dynamicFields[name];

  useEffect(() => {
    setOptions(
      (data ?? [])
        .sort((m1, m2) => m1.name.toLowerCase().localeCompare(m2.name.toLowerCase()))
        .map((member) => ({
          id: member.id,
          name: member.name,
          member,
        })),
    );
  }, [data]);

  // Supports single or multi select of record titles based on the properties of the
  // Assigned To field from the form
  const isMultiple = field.params?.entries_allowed === "multiple";

  const handleSelectChange = (changeValue: Member | Member[]): void => {
    if (changeValue) {
      if (Array.isArray(changeValue)) {
        formField.onChange(changeValue);
      } else {
        formField.onChange([changeValue]);
      }
    } else {
      formField.onChange([]);
    }
  };

  useEffect(() => {
    if (prefillValue && !isLoading && formField.value === undefined) {
      const parsedPrefillValue = prefillAssignTo({
        value: prefillValue,
        field,
        members: data ?? [],
      });

      handleSelectChange(parsedPrefillValue as Member[]);
    }
  }, [prefillValue, isLoading]);

  if (!isItemConditionSatisfied) return null;

  return (
    <FormFieldControl
      captionAsTooltip={formItem.params.help_text_as_tooltip}
      caption={caption}
      errorMessage={formField.errorMessage}
      label={label}
      helpText={formItem.params.helpText}
      required={required}
      state={formField.state}
      readOnly={readOnly}
    >
      {!isMultiple && (
        <KeyValueCustomSelect<"member", "name", AssignToFormValue>
          className="assigned-to-field-control__select"
          inputBackground="white-new"
          placeholderArrowStyle="arrow-down-smaller"
          clearIconName="close-thin"
          disabled={readOnly}
          isReadOnly={readOnly}
          allowSelection={!readOnly}
          labelField="name"
          options={options}
          showPlaceholderArrow={!readOnly}
          placeholder={
            isLoading ? $t(fieldsStateText.loading) : $t(fieldsText.singleSelectPlaceholder)
          }
          searchPlaceholder={$t(fieldsText.singleSelectSearchOptionPlaceholder)}
          showFilterGreaterThan={4}
          state={formField.state}
          value={formField.value?.[0]}
          valueField="member"
          valueFieldPath="id"
          virtualized={true}
          itemHeight={36}
          onChange={handleSelectChange}
          onChangeFocused={formField.onChangeFocused}
        />
      )}

      {isMultiple && (
        <KeyValueCustomMultipleSelect<"member", "name", AssignToFormValue>
          inputGroupClassname="assigned-to-field-control-multiple-select"
          inputBackground="white-new"
          placeholderArrowStyle="arrow-down-smaller"
          placeholderClassMod="forms"
          labelField="name"
          disabled={readOnly}
          isReadOnly={readOnly}
          showDropdownArrow={!readOnly}
          options={options}
          placeholder={
            isLoading ? $t(fieldsStateText.loading) : $t(fieldsText.multipleSelectPlaceholder)
          }
          renderSelectedOption={(option) => <Pill text={option.name} />}
          searchPlaceholder={$t(fieldsText.multipleSelectSearchOptionPlaceholder)}
          showFilterGreaterThan={4}
          state={formField.state}
          value={formField.value}
          valueField="member"
          valueFieldPath="id"
          virtualized={true}
          itemHeight={36}
          onChange={handleSelectChange}
          onChangeFocused={formField.onChangeFocused}
        />
      )}
    </FormFieldControl>
  );
};
